import { MenuItem } from "@mui/material";
import Input from "components/input-fields/input-fields";
import React from "react";
import { debounce } from "lodash";
import SearchInput from "components/search-input/search-input";

import { CustomerTicketsParams, DateFilter, TicketStatus, useTickets } from "pages/tickets/ticket.service";
import TicketCardComponent from "pages/tickets/component/ticket-card.component";
import TicketDetailsComponent from "pages/tickets/component/ticket-details.component";
import TicketChatComponent from "pages/tickets/component/ticket-chat.component";

import { useModal } from "hooks";
import {useLocation, useNavigate} from "react-router-dom";
import NewTicketDialog from "pages/tickets/component/new-ticket.dialog";
import { Spinner } from "react-bootstrap";
import useInfiniteScroll from "react-infinite-scroll-hook";

import SortByComponent from "components/sort-by/sort-by.component";
import { useTranslation } from "react-i18next";
import CanLoadGuard from "components/private-route/can-load.guard";

const InitialCustomerTicketsParams: CustomerTicketsParams = {
  StatusId: 0,
  Search: "",
  FromDate: "",
  ToDate: "",
  PageNumber: 1,
  PageSize: 4
};

const TicketsPage = () => {
  const { t } = useTranslation("tickets");
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const [ticketId, setTicketId] = React.useState("");
  // use for getting one ticket by serial;
  // const { ticketSerial } = useParams();
  const [filters, setFilters] = React.useState<CustomerTicketsParams>(InitialCustomerTicketsParams);
  const { tickets, totalCount, isLoading, fetchNextPage, hasNextPage = false, isFetchingNextPage } = useTickets(filters);
  const [isAddingTicket, setIsAddingTicket] = React.useState(state?.isAddingTicket ?? false);
  const [showNewTicket, openNewTicket, closeNewTicket] = useModal(isAddingTicket);

  const [infiniteRef] = useInfiniteScroll({
    hasNextPage,
    loading: isFetchingNextPage,
    onLoadMore: fetchNextPage
  });

  function handleSearch(query: string) {
    setTicketId("");
    setFilters((f) => ({ ...f, Search: query }));
  }

  function handleSortByTicketStatus(e: React.ChangeEvent<HTMLInputElement>) {
    const statusId = e.target.value;
    setTicketId("");
    setFilters((f) => ({ ...f, StatusId: +statusId }));
  }

  function handleSortByDate(dateString: any) {
    const date = typeof dateString === "string" ? JSON.parse(dateString) : dateString;
    setTicketId("");
    setFilters((f) => ({ ...f, FromDate: date.from, ToDate: date.to }));
  }

  React.useEffect(() => {
    if (!ticketId) setTicketId(tickets[0]?.tickets[0]?.id ?? "");
  }, [ticketId, tickets]);

  return (
    <div className="tickets">
      <div className="header">
        <div className="page-name">
          <h1>
            {t("tickets") as string} ({totalCount})
          </h1>
        </div>
        <div className="action">
          <SortByComponent
            defaultValue={JSON.stringify(DateFilter.all)}
            list={Object.entries(DateFilter).map(([key, value]) => ({
              value: JSON.stringify(value),
              label: t(key)
            }))}
            onChange={handleSortByDate}
            date
          />
          <Input type="select" Class="status" label={t("status") + " : "} onChange={handleSortByTicketStatus} defaultValue={0}>
            <MenuItem value="0">{t("all")}</MenuItem>
            {Object.entries(TicketStatus).map(([k, v]) => (
              <MenuItem key={v} value={v}>
                {t(k)}
              </MenuItem>
            ))}
          </Input>
          <SearchInput
            onChange={(q) => {
              const handle = debounce(() => handleSearch(q), 2000);
              handle();
            }}
          />
          <CanLoadGuard>
          <button className="btn-green" onClick={openNewTicket}>
            + {t("new-ticket")}
          </button>
          </CanLoadGuard>
        </div>
      </div>
      <hr className="hr" />

      <div className="row">
        <div className="col-xl-3 col-md-6 col-sm-6 order-1 order-xl-1">
          <div className="ticket-left-side" ref={infiniteRef}>
            {isLoading && <Spinner animation="grow" variant="primary" />}
            {!isLoading && !tickets.length && <p>Not Available</p>}
            {tickets.map((page) =>
              page?.tickets.map((ticket) => (
                <TicketCardComponent
                  key={ticket.id}
                  onSelect={() => setTicketId(ticket.id!)}
                  selected={ticketId === ticket.id!}
                  ticket={ticket}
                />
              ))
            )}
            {isFetchingNextPage && "loading..."}
          </div>
        </div>
        <div className="col-xl-6 order-3 order-xl-2 dataTable">
          <TicketChatComponent key={ticketId} ticketId={ticketId} />
        </div>
        <div className="col-xl-3 col-md-6 col-sm-6 order-2 order-xl-3">
          <TicketDetailsComponent ticketId={ticketId} />
        </div>
        <NewTicketDialog
          show={showNewTicket}
          onHide={() => {
            navigate('.');
            closeNewTicket();
            setIsAddingTicket(false);
          }}
          isAddingTicket={isAddingTicket}
        />
      </div>
    </div>
  );
};

export default TicketsPage;
